import { useLocation, useNavigate, useParams } from "react-router-dom";
import PageHeader from "../../components/PageHeader/PageHeader";
import { useCallback, useEffect, useMemo, useState } from "react";
import auth from "../../fire";
import { useAuthState } from "react-firebase-hooks/auth";
import axiosClient from "../../axiosClient";
import MessageModal from "../../components/MessageModal/MessageModal";
import ExerciseList from "../../components/ExerciseList/ExerciseList";
import TextSkeleton from "../../components/SkeletonComponents/TextSkeleton";
import { format } from "date-fns";
import DeleteModal from "../../components/DeleteModal/DeleteModal";
import NotesInput from "../../components/NotesInput/NotesInput";
import FullWidthAsyncButton from "../../components/FullWidthButton/FullWidthAsyncButton";

const WorkoutDetailsPage: React.FC = () => {
  const { workoutId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const routedState = location.state;
  const routedWorkoutTitle = routedState?.workoutTitle;
  const routedWorkoutDate = routedState?.workoutDate;

  const [workoutData, setWorkoutData] = useState<Workout | null>(null);

  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const [user, loading] = useAuthState(auth);

  interface DeleteModalProps {
    isOpen: boolean;
    exerciseToDelete: string | null;
  }

  const [deleteModalState, setDeleteModalState] = useState<DeleteModalProps>({
    isOpen: false,
    exerciseToDelete: null,
  });

  const [modalState, setModalState] = useState({
    isOpen: false,
    type: "",
    title: "",
    message: "",
  });

  const [newExerciseLoading, setNewExerciseLoading] = useState(false);

  const fetchWorkoutData = useCallback(async () => {
    try {
      const fetchWorkoutResponse = await axiosClient.get(
        `/api/workouts/${workoutId}`,
        {
          params: { populateExercises: true },
        }
      );
      const workoutData = fetchWorkoutResponse.data;

      setWorkoutData(workoutData);
      setIsInitialLoading(false);
    } catch (e: any) {
      console.error(e);
      const errorMessage =
        e?.response?.data?.error || "Something unexpected occured.";
      setModalState({
        isOpen: true,
        type: "error",
        title: "Error Getting Workout Details",
        message: `${errorMessage} Please try again or contact support.`,
      });
      setIsInitialLoading(false);
    }
  }, [workoutId]);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login");
      return;
    }

    fetchWorkoutData();
  }, [user, loading, navigate, fetchWorkoutData]);

  if (isInitialLoading || !workoutData) {
    return (
      <WorkoutDetailsPageLoadingState
        routedWorkoutTitle={routedWorkoutTitle}
        routedWorkoutDate={routedWorkoutDate}
      />
    );
  }

  const optimisticWorkoutUpdate = async (updateObject: DraftWorkout) => {
    try {
      const updateWorkoutResponse = await axiosClient.put(
        `/api/workouts/${workoutId}`,
        updateObject
      );

      console.log(updateWorkoutResponse);
      if (updateWorkoutResponse.status !== 200) {
        throw new Error(updateWorkoutResponse.data.message);
      }
    } catch (e) {
      console.error("Error updating workout: " + e);
      setModalState({
        isOpen: true,
        type: "error",
        title: "Error Updating Workout",
        message:
          "An update to this workout failed. Please try again or contact support.",
      });
    }
  };

  const handleDeleteModalSubmit = async (exerciseToDeleteId: string) => {
    setDeleteModalState({ isOpen: false, exerciseToDelete: null });

    const newExercisesArray: Exercise[] = workoutData.exercises.filter(
      (exercise) => {
        return exercise._id !== exerciseToDeleteId;
      }
    );

    setWorkoutData((prevWorkoutData) => {
      if (prevWorkoutData === null) return null;

      return {
        ...prevWorkoutData,
        exercises: newExercisesArray,
      };
    });

    try {
      const deleteExerciseResponse = await axiosClient.delete(
        `/api/exercises/${exerciseToDeleteId}`
      );

      if (deleteExerciseResponse.status !== 200) {
        throw new Error(deleteExerciseResponse.data.message);
      }
    } catch (e) {
      console.error("Error deleting exercise: " + e);
      setModalState({
        isOpen: true,
        type: "error",
        title: "Error Deleting Exercise",
        message:
          "Deleting exercise has failed! The change may not have been saved correctly. Please try again or contact support.",
      });
    }
  };

  const handleExerciseCreated = async () => {
    setNewExerciseLoading(true);
    const newExercise = {
      workoutId: workoutData.id,
      name: "",
      sets: [],
      date: workoutData.date,
    };

    try {
      const createExerciseResponse = await axiosClient.post(
        `/api/exercises/`,
        newExercise
      );

      if (createExerciseResponse.status !== 200) {
        throw new Error(createExerciseResponse.data.message);
      }

      const newExerciseId = createExerciseResponse.data.id;
      navigate(`/workout/${workoutData.id}/exercise/${newExerciseId}`);
    } catch (e) {
      console.error("Error creating exercise: " + e);
      setModalState({
        isOpen: true,
        type: "error",
        title: "Error Creating Exercise",
        message: "Creating exercise has failed! Page will now refresh.",
      });
    }
  };

  const handleExercisesReordered = (reorderedExerciseArray: Exercise[]) => {
    setWorkoutData((prevWorkoutData) => {
      if (prevWorkoutData === null) return null;

      return {
        ...prevWorkoutData,
        exercises: reorderedExerciseArray,
      };
    });
    optimisticWorkoutUpdate({ exercises: [...reorderedExerciseArray] });
  };

  const handleNotesUpdated = (newNotes: string) => {
    setWorkoutData((prevWorkoutData) => {
      if (prevWorkoutData === null) return null;

      return {
        ...prevWorkoutData,
        notes: newNotes,
      };
    });

    optimisticWorkoutUpdate({ notes: newNotes });
  };

  return (
    <>
      <PageHeader backLink={`/`} pageTitle="Workout Details" />
      <div className="h-[calc(100%-63px)] overflow-auto">
        <div className="p-2">
          <h2 className="font-bold leading-7 text-gray-900 text-xl tracking-tight  text-ellipsis overflow-hidden whitespace-nowrap">
            {workoutData.title || "Untitled Workout"}
          </h2>
          <h3 className="text-ellipsis overflow-hidden whitespace-nowrap text-sm text-gray-500">
            {format(new Date(workoutData.date), "iii, MMMM d, yyyy")}
          </h3>
        </div>
        <ExerciseList
          currentWorkoutAndExercises={workoutData}
          onExerciseCreated={handleExerciseCreated}
          onExercisesReordered={handleExercisesReordered}
          onDeleteExercise={(exerciseId: string) => {
            setDeleteModalState({ isOpen: true, exerciseToDelete: exerciseId });
          }}
          createExerciseLoading={newExerciseLoading}
        />
        <NotesInput
          placeholder="Put any notes for this workout here! &#10;Examples: supersets, skipped exercises, additional work, how strong you're feeling, etc."
          notes={workoutData.notes}
          onNotesUpdated={handleNotesUpdated}
        />
      </div>
      <DeleteModal
        isOpen={deleteModalState.isOpen}
        itemToDeleteId={deleteModalState.exerciseToDelete}
        onModalClose={() => {
          setDeleteModalState({
            isOpen: false,
            exerciseToDelete: null,
          });
        }}
        onSubmit={handleDeleteModalSubmit}
        deleteTitle={"Delete exercise"}
        deleteMessage={
          "Are you sure you want to delete? This exercise and all its set data will be permanently removed. This action cannot be undone."
        }
      />
      <MessageModal
        isOpen={modalState.isOpen}
        onModalClose={() => {
          setModalState({ ...modalState, isOpen: false });
        }}
        type={modalState.type}
        title={modalState.title}
        message={modalState.message}
      />
    </>
  );
};

interface WorkoutDetailsPageLoadingStateProps {
  routedWorkoutTitle: string;
  routedWorkoutDate: string;
}

const WorkoutDetailsPageLoadingState: React.FC<
  WorkoutDetailsPageLoadingStateProps
> = ({ routedWorkoutTitle, routedWorkoutDate }) => {
  const { innerHeight: screenHeight } = window;

  const LOGO_HEADER = 63;
  const WORKOUT_TITLE_DATE = 64;
  const ADD_EXERCISE_BTN = 62;
  const NOTES_INPUT = 138;
  const MAIN_NAV = 63;

  const NON_EXERCISE_LIST_CONTENT_HEIGHT =
    LOGO_HEADER +
    WORKOUT_TITLE_DATE +
    ADD_EXERCISE_BTN +
    NOTES_INPUT +
    MAIN_NAV;

  const amountOfCardSkeletons = useMemo(() => {
    const listHeight = screenHeight - NON_EXERCISE_LIST_CONTENT_HEIGHT;
    return Math.floor(listHeight / 60) - 1;
  }, [NON_EXERCISE_LIST_CONTENT_HEIGHT, screenHeight]);

  return (
    <>
      <PageHeader backLink={`/`} pageTitle="Workout Details" />
      <div className="p-2">
        {routedWorkoutTitle ? (
          <h2 className="font-bold leading-7 text-gray-900 text-xl tracking-tight  text-ellipsis overflow-hidden whitespace-nowrap">
            {routedWorkoutTitle}
          </h2>
        ) : (
          <TextSkeleton height="20px" className="w-full mt-[4px] mb-[7px]" />
        )}
        {routedWorkoutDate ? (
          <h3 className="text-ellipsis overflow-hidden whitespace-nowrap text-sm text-gray-500">
            {format(new Date(routedWorkoutDate), "iii, MMMM d, yyyy")}
          </h3>
        ) : (
          <TextSkeleton height="14px" className="w-full mb-[3px]" />
        )}
      </div>
      <div className="px-2">
        {Array.from({ length: amountOfCardSkeletons }, (number, index) => (
          <TextSkeleton key={index} height="60px" className="w-full mb-1" />
        ))}
        <div className="pt-2 pb-4">
          <FullWidthAsyncButton
            isSkeleton={true}
            text="Add Exercise"
            loadingText=""
            onClick={async () => {}}
          />
        </div>
      </div>
      <NotesInput
        placeholder="Put any notes for this workout here! &#10;Examples: supersets, skipped exercises, additional work, how strong you're feeling, etc."
        isLoading={true}
      />
    </>
  );
};

export default WorkoutDetailsPage;
